import { BUILTIN_FILE_ICON_THEMES, BUILTIN_PRODUCT_ICON_THEMES } from "../manifests/icon-themes/builtin";
import { BUILTIN_PROFILES } from "../manifests/profiles/builtin";
import { BUILTIN_COLOR_THEMES } from "../manifests/themes/builtin";
import type {
    ColorThemeManifest,
    FileIconThemeManifest,
    ProductIconThemeManifest,
    ProfileManifest,
    ThemeKind,
} from "../contracts";
import { createFileIconThemeRegistry } from "../registry/file-icon-theme-registry";
import type { ProductIconSlotId } from "../registry/icon-slot-registry";
import { createProductIconThemeRegistry } from "../registry/product-icon-theme-registry";
import {
    type ThemeColorTokenId,
} from "../registry/color-registry";
import type { SemanticTokenId } from "../registry/semantic-token-registry";
import type { SyntaxTokenId } from "../registry/syntax-token-registry";
import { createProfileRegistry, type ProfileTokenId } from "../registry/profile-registry";

const COLOR_THEME_REGISTRY = new Map(
    BUILTIN_COLOR_THEMES.map((manifest) => [manifest.id, manifest]),
) as Map<
    string,
    ColorThemeManifest<ThemeColorTokenId, SyntaxTokenId, SemanticTokenId>
>;

const PRODUCT_ICON_THEME_REGISTRY = createProductIconThemeRegistry(
    BUILTIN_PRODUCT_ICON_THEMES,
);
const FILE_ICON_THEME_REGISTRY = createFileIconThemeRegistry(
    BUILTIN_FILE_ICON_THEMES,
);
const PROFILE_REGISTRY = createProfileRegistry(BUILTIN_PROFILES);

export const DEFAULT_COLOR_THEME_ID = "magicmd.workbench-light";
export const DEFAULT_PRODUCT_ICON_THEME_ID = "magicmd.default-product-icons";
export const DEFAULT_FILE_ICON_THEME_ID = "magicmd.default-file-icons";
export const DEFAULT_CONTENT_PROFILE_ID = "magicmd.documentation";
export const DEFAULT_PRESENTATION_PROFILE_ID = "magicmd.paper";

export function getColorThemeManifest(
    themeId: string,
): ColorThemeManifest<ThemeColorTokenId, SyntaxTokenId, SemanticTokenId> {
    return (
        COLOR_THEME_REGISTRY.get(themeId) ??
        COLOR_THEME_REGISTRY.get(DEFAULT_COLOR_THEME_ID)!
    );
}

export function getProductIconThemeManifest(
    themeId: string,
): ProductIconThemeManifest<ProductIconSlotId> {
    return (
        PRODUCT_ICON_THEME_REGISTRY.get(themeId) ??
        PRODUCT_ICON_THEME_REGISTRY.get(DEFAULT_PRODUCT_ICON_THEME_ID)!
    );
}

export function getFileIconThemeManifest(themeId: string): FileIconThemeManifest {
    return (
        FILE_ICON_THEME_REGISTRY.get(themeId) ??
        FILE_ICON_THEME_REGISTRY.get(DEFAULT_FILE_ICON_THEME_ID)!
    );
}

export function getProfileManifest(
    profileId: string,
    scope: ProfileManifest<ProfileTokenId>["scope"],
): ProfileManifest<ProfileTokenId> {
    const fallbackId =
        scope === "content"
            ? DEFAULT_CONTENT_PROFILE_ID
            : DEFAULT_PRESENTATION_PROFILE_ID;
    const fallback = PROFILE_REGISTRY.get(fallbackId)!;
    const candidate = PROFILE_REGISTRY.get(profileId);
    return candidate?.scope === scope ? candidate : fallback;
}

export function resolveThemeKind(themeId: string): ThemeKind {
    const manifest = COLOR_THEME_REGISTRY.get(themeId);
    if (manifest) return manifest.kind;

    const normalized = themeId.toLowerCase();
    if (
        normalized.includes("hc-light") ||
        normalized.includes("high-contrast-light")
    ) {
        return "hc-light";
    }
    if (normalized.includes("hc") || normalized.includes("high-contrast")) {
        return "hc-dark";
    }
    if (normalized.includes("dark")) return "dark";
    return "light";
}

export function listColorThemeIds(): string[] {
    return Array.from(COLOR_THEME_REGISTRY.keys());
}

export function listProductIconThemeIds(): string[] {
    return Array.from(PRODUCT_ICON_THEME_REGISTRY.keys());
}

export function listFileIconThemeIds(): string[] {
    return Array.from(FILE_ICON_THEME_REGISTRY.keys());
}

export function listProfileIds(
    scope: ProfileManifest<ProfileTokenId>["scope"],
): string[] {
    return Array.from(PROFILE_REGISTRY.values())
        .filter((manifest) => manifest.scope === scope)
        .map((manifest) => manifest.id);
}
